'use client';

import { MathManager } from '@payments-checkout/shared/helpers/managers/mathManager';
import { SummaryProps } from '../../types/sumaryCard.types';
import { useSessionStatus } from '../useSessionStatus';

interface DetailItem {
  name: string;
  description: string;
  unit: number;
  unitDescription: string;
  value: string;
}

interface SummaryData {
  items: SummaryProps['items'];
  total: {
    amount: number;
    description: string;
    formattedAmount: string;
    discountValue?: string;
    incrementValue?: string;
  };
}

export const useStatusPageSharedData = (sessionId?: string) => {
  const { data: responseData, isLoading, error, isFetched } = useSessionStatus(sessionId);
  const data = responseData?.data;

  const buildDetailItems = (): DetailItem[] => {
    if (!data?.items) return [];

    return data.items.map((item) => ({
      name: item.name,
      description: '',
      unit: item.quantity,
      unitDescription: item.quantity === 1 ? 'info.singleUnit.label' : 'info.multipleUnit.label',
      value: MathManager.formatCurrency({ value: item.amount / 100 }),
    }));
  };

  const buildSummary = (): SummaryData => {
    if (!data?.summary) {
      return {
        items: [],
        total: {
          amount: 0,
          description: 'total',
          formattedAmount: '--',
        },
      };
    }

    const returnValues: Record<string, string> = {};

    if (data.summary.subtotal !== 0) {
      returnValues.subtotal = MathManager.formatCurrency({
        value: data.summary.subtotal / 100,
      });
    }

    if (data?.summary?.shipping && data?.summary?.shipping > 0) {
      returnValues.shipping = MathManager.formatCurrency({
        value: data.summary.shipping / 100,
      });
    }

    const total = data.summary.total;
    const formattedTotal = MathManager.formatCurrency({ value: total / 100 });

    const items: SummaryProps['items'] = Object.entries(returnValues).map(
      ([key, value]) => ({
        title: key,
        description: {
          color: key === 'discount' ? 'positive' : 'medium',
          weight: 'semibold',
          content: value,
        },
      })
    );

    let discountValue: string | undefined;
    if (data?.summary?.discount && data?.summary?.discount > 0) {
      discountValue = MathManager.formatCurrency({
        value: -(data.summary.discount / 100),
      });
    }

    let incrementValue: string | undefined;
    if (data?.summary?.increment &&data?.summary?.increment > 0) {
      incrementValue = MathManager.formatCurrency({
        value: data.summary.increment / 100,
      });
    }

    return {
      items,
      total: {
        amount: total,
        description: 'total',
        formattedAmount: total === 0 ? '--' : formattedTotal,
        discountValue,
        incrementValue,
      },
    };
  };

  const isReady = isFetched && !!data;
  const detailItems = buildDetailItems();
  const summary = buildSummary();

  return {
    isLoading,
    error,
    isReady,
    detailItems,
    items: summary.items,
    total: summary.total,
    sessionData: data,
  };
};
